import { useState } from 'react'
import { HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { BxsCheckCircle } from '@opengovsg/design-system-react'
import { useMutation } from '@tanstack/react-query'

import { FEATURE_FLAGS } from '~shared/constants'
import { SubmitRespondentCsatReq } from '~shared/dto'

import { api } from '~lib/api'
import { CsatForm } from '~components/CsatForm'

export interface RespondentCsatProps {
  bookingId: string
}

/**
 * Inline CSAT widget on the booking confirmation page. Insert-only:
 * one POST on submit, then a thank-you state. Failures degrade to the
 * thank-you state too - a rating widget should never surface an error.
 */
export const RespondentCsat = ({ bookingId }: RespondentCsatProps) => {
  const isEnabled = useFeatureIsOn(FEATURE_FLAGS.RESPONDENT_CSAT)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { mutate, isLoading } = useMutation(
    (body: SubmitRespondentCsatReq) =>
      api.url(`/bookings/${bookingId}/csat`).post(body).res(),
    { onSettled: () => setIsSubmitted(true) },
  )

  if (!isEnabled) return null

  if (isSubmitted) {
    return (
      <HStack w="full">
        <Icon
          as={BxsCheckCircle}
          fontSize="1.25rem"
          color="utility.feedback.success"
        />
        <Text textStyle="subhead-2">Thank you for your feedback!</Text>
      </HStack>
    )
  }

  return (
    <VStack w="full" alignItems="start" spacing={2}>
      <Text textStyle="subhead-1">How was your booking experience?</Text>
      <CsatForm onSubmit={mutate} isSubmitting={isLoading} />
    </VStack>
  )
}
